// frontend/src/modules/onboarding/registry.ts
//
// Presentation for each onboarding step: the checklist copy, where the item
// links, and the spotlight stops its walkthrough runs.
//
// The server decides WHICH steps a user sees and WHETHER each is done. This
// file only decides how they read and where they point. Key strings are the
// contract; registry.test.ts pins them to the server's set.

import type { TourStop } from './TourProvider';
import type { OnboardingStepKey } from './types';

export interface StepPresentation {
  key: OnboardingStepKey;
  /** Checklist row title. */
  label: string;
  /** One line of why it matters. */
  blurb: string;
  /** Where "Show me" navigates if there are no stops. */
  to: string;
  stops: TourStop[];
}

export const STEP_PRESENTATION: Record<OnboardingStepKey, StepPresentation> = {
  'workspace.details': {
    key: 'workspace.details',
    label: 'Add your company details',
    blurb: 'Your logo and address appear on every invoice you send.',
    // ?section=company is load-bearing: the Company section (which holds the
    // anchor) is not mounted on the default Profile section.
    to: '/dashboard/settings?section=company',
    stops: [{
      anchor: 'workspace.details',
      route: '/dashboard/settings?section=company',
      title: 'Your company details',
      body: 'Add your logo and billing address here — they appear on every invoice and email your clients receive.',
    }],
  },
  'clients.first': {
    key: 'clients.first',
    label: 'Add your first client',
    blurb: 'Clients are who you invoice.',
    to: '/dashboard/clients',
    stops: [{
      anchor: 'clients.new',
      route: '/dashboard/clients',
      title: 'Add your first client',
      body: 'A client is whoever you bill. Add one now — you will pick them when you create an invoice.',
    }],
  },
  'services.first': {
    key: 'services.first',
    label: 'Set up your services and prices',
    blurb: 'Saved services fill in invoice lines for you.',
    to: '/dashboard/services',
    stops: [{
      anchor: 'services.new',
      route: '/dashboard/services',
      title: 'Set up a service',
      body: 'Save the things you sell with their prices, and invoice lines fill themselves in from here.',
    }],
  },
  'invoices.first': {
    key: 'invoices.first',
    label: 'Create your first invoice',
    blurb: 'Pick a client, add your services, send it.',
    to: '/dashboard/invoices',
    stops: [{
      anchor: 'invoices.new',
      route: '/dashboard/invoices',
      title: 'Create an invoice',
      body: 'Choose the client you added, drop in your services, and send. Payments recorded against it show on your dashboard.',
    }],
  },
  'team.invite': {
    key: 'team.invite',
    label: 'Invite your team',
    blurb: 'Give people the access they need — and nothing more.',
    to: '/dashboard/settings/access',
    stops: [{
      anchor: 'team.invite',
      route: '/dashboard/settings/access',
      title: 'Invite your team',
      body: 'Invite people by email and pick a role. Roles decide which modules and actions each person gets.',
    }],
  },
  'attendance.first': {
    key: 'attendance.first',
    label: 'Clock in for the first time',
    blurb: 'Your hours feed attendance and payroll.',
    to: '/dashboard/hr/attendance',
    stops: [],
  },
  'schedule.view': {
    key: 'schedule.view',
    label: 'See your schedule',
    blurb: 'Your shifts for the next two weeks.',
    to: '/dashboard/hr/my-schedule',
    stops: [],
  },
  'payslips.view': {
    key: 'payslips.view',
    label: 'Find your payslips',
    blurb: 'Every payslip issued to you, whenever you need it.',
    to: '/dashboard/payroll',
    stops: [],
  },
};
